import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AGE_BANDS,
  GUEST_SIDES,
  RSVP_STATUSES,
  type AgeBand,
  type Guest,
  type GuestSide,
  type Household,
  type RsvpStatus,
} from '@guabi/shared';
import { Button } from '@/components/ui/Button';
import { Drawer } from '@/components/ui/Drawer';
import { Field, SelectInput, TextInput } from '@/components/ui/Field';
import { ApiRequestError } from '@/lib/api';
import { HouseholdDetailsInline } from './HouseholdName';
import { useAddHouseholdMember, useCreateGuest, useDeleteGuest, useUpdateGuest } from './queries';

const NEW_HOUSEHOLD = '__new__';

/**
 * Add or edit one person. The mailing address is not here — it belongs to the
 * household — and neither is a plus-one name, because a plus-one is a person of
 * their own now.
 */
export function GuestDrawer({
  wedId,
  guest,
  households,
  open,
  onClose,
  canWrite = true,
}: {
  wedId: string;
  /** Absent when adding. */
  guest?: Guest;
  households: Household[];
  open: boolean;
  onClose: () => void;
  /** A read-only member may open this to read it, and change nothing. */
  canWrite?: boolean;
}) {
  const { t } = useTranslation();
  const create = useCreateGuest(wedId);
  const update = useUpdateGuest(wedId);
  const remove = useDeleteGuest(wedId);
  const addMember = useAddHouseholdMember(wedId);

  const [firstName, setFirstName] = useState(guest?.isPlaceholder ? '' : (guest?.firstName ?? ''));
  const [lastName, setLastName] = useState(guest?.lastName ?? '');
  const [email, setEmail] = useState(guest?.email ?? '');
  const [phone, setPhone] = useState(guest?.phone ?? '');
  const [side, setSide] = useState<GuestSide>(guest?.side ?? 'shared');
  const [rsvp, setRsvp] = useState<RsvpStatus>(guest?.rsvp ?? 'pending');
  const [ageBand, setAgeBand] = useState<AgeBand>(guest?.ageBand ?? 'adult');
  const [householdId, setHouseholdId] = useState(guest?.householdId ?? NEW_HOUSEHOLD);
  const [tags, setTags] = useState((guest?.tags ?? []).join(', '));
  const [dietaryNotes, setDietary] = useState(guest?.dietaryNotes ?? '');
  const [notes, setNotes] = useState(guest?.notes ?? '');
  const [error, setError] = useState<string>();

  const busy = create.isPending || update.isPending || remove.isPending || addMember.isPending;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(undefined);

    const fields = {
      firstName,
      lastName,
      email: email || undefined,
      phone,
      side,
      rsvp,
      ageBand,
      tags: tags
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
      dietaryNotes,
      notes,
      ...(householdId !== NEW_HOUSEHOLD ? { householdId } : {}),
    };

    try {
      if (guest) {
        await update.mutateAsync({
          guestId: guest.guestId,
          input: { ...fields, updatedAt: guest.updatedAt },
        });
      } else {
        await create.mutateAsync(fields);
      }
      onClose();
    } catch (err) {
      setError(
        err instanceof ApiRequestError && err.code === 'version_conflict'
          ? t('guests.conflict')
          : t('common.somethingWrong'),
      );
    }
  }

  /** "And guest": a real row nobody has named, in the same household. */
  async function onAddPlaceholder() {
    if (!guest?.householdId) return;
    setError(undefined);
    try {
      await addMember.mutateAsync({
        householdId: guest.householdId,
        input: {
          firstName: 'Guest',
          isPlaceholder: true,
          placeholderFor: guest.guestId,
          side: guest.side,
          ageBand: 'adult',
        },
      });
      onClose();
    } catch {
      setError(t('common.somethingWrong'));
    }
  }

  async function onDelete() {
    if (!guest) return;
    setError(undefined);
    try {
      await remove.mutateAsync({ guestId: guest.guestId });
      onClose();
    } catch {
      setError(t('common.somethingWrong'));
    }
  }

  const title = guest
    ? guest.isPlaceholder
      ? t('guests.nameThemTitle')
      : t('guests.editTitle')
    : t('guests.addTitle');

  return (
    /*
     * Pinned, not inline. This form is long — it carries the household's name,
     * address and gift as well as the person — and a Save at the end of it is
     * one you scroll past other buttons to reach.
     */
    <Drawer
      open={open}
      title={title}
      onClose={onClose}
      footer={
        <div className="flex flex-wrap items-center gap-3">
          {canWrite && (
            <Button type="submit" form="guest-form" loading={busy}>
              {t('common.save')}
            </Button>
          )}
          <Button type="button" variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
        </div>
      }
    >
      <form id="guest-form" onSubmit={onSubmit} className="flex flex-col gap-4">
        {/* One attribute instead of thirty: a disabled fieldset disables every
            control inside it, so a read-only member reads the real values and
            can change none of them. `contents` keeps it out of the layout. */}
        <fieldset disabled={!canWrite} className="contents">
          {guest?.isPlaceholder && (
            <p className="text-body text-leaf-soft">{t('guests.nameThemBody')}</p>
          )}

          <Field label={t('guests.firstName')}>
            {({ id }) => (
              <TextInput
                id={id}
                required
                maxLength={200}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            )}
          </Field>

          <Field label={t('guests.lastName')}>
            {({ id }) => (
              <TextInput
                id={id}
                maxLength={200}
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            )}
          </Field>

          <Field label={t('guests.ageBand')} hint={t('guests.ageBandHint')}>
            {({ id, describedBy }) => (
              <SelectInput
                id={id}
                aria-describedby={describedBy}
                value={ageBand}
                onChange={(e) => setAgeBand(e.target.value as AgeBand)}
              >
                {AGE_BANDS.map((a) => (
                  <option key={a} value={a}>
                    {t(`guests.ageBandValue.${a}`)}
                  </option>
                ))}
              </SelectInput>
            )}
          </Field>

          <Field label={t('guests.household')} hint={t('guests.householdHint')}>
            {({ id, describedBy }) => (
              <SelectInput
                id={id}
                aria-describedby={describedBy}
                value={householdId}
                onChange={(e) => setHouseholdId(e.target.value)}
              >
                <option value={NEW_HOUSEHOLD}>{t('guests.newHouseholdOfOne')}</option>
                {households.map((h) => (
                  <option key={h.householdId} value={h.householdId}>
                    {h.label}
                  </option>
                ))}
              </SelectInput>
            )}
          </Field>

          <HouseholdDetailsInline
            wedId={wedId}
            household={households.find((h) => h.householdId === householdId)}
          />

          <Field label={t('guests.rsvp')}>
            {({ id }) => (
              <SelectInput
                id={id}
                value={rsvp}
                onChange={(e) => setRsvp(e.target.value as RsvpStatus)}
              >
                {RSVP_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {t(`guests.rsvpValue.${s}`)}
                  </option>
                ))}
              </SelectInput>
            )}
          </Field>

          <Field label={t('guests.side')}>
            {({ id }) => (
              <SelectInput
                id={id}
                value={side}
                onChange={(e) => setSide(e.target.value as GuestSide)}
              >
                {GUEST_SIDES.map((s) => (
                  <option key={s} value={s}>
                    {t(`guests.sideValue.${s}`)}
                  </option>
                ))}
              </SelectInput>
            )}
          </Field>

          <Field label={t('guests.email')}>
            {({ id }) => (
              <TextInput
                id={id}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            )}
          </Field>

          <Field label={t('guests.phone')}>
            {({ id }) => (
              <TextInput
                id={id}
                type="tel"
                maxLength={40}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            )}
          </Field>

          <Field label={t('guests.tags')} hint={t('guests.tagsHint')}>
            {({ id, describedBy }) => (
              <TextInput
                id={id}
                aria-describedby={describedBy}
                value={tags}
                onChange={(e) => setTags(e.target.value)}
              />
            )}
          </Field>

          <Field label={t('guests.dietaryNotes')}>
            {({ id }) => (
              <TextInput
                id={id}
                maxLength={5000}
                value={dietaryNotes}
                onChange={(e) => setDietary(e.target.value)}
              />
            )}
          </Field>

          <Field label={t('guests.notes')}>
            {({ id }) => (
              <TextInput
                id={id}
                maxLength={5000}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            )}
          </Field>

          {error && (
            <p role="alert" className="text-body text-status-declinedFg">
              {error}
            </p>
          )}

          {/* What is left inline is everything that is not Save: one makes a
            second person, one removes this one. Neither belongs under a thumb
            that is reaching for the button it always presses. */}
          <div className="flex flex-wrap gap-3">
            {guest && !guest.isPlaceholder && (
              <Button type="button" variant="ghost" onClick={() => void onAddPlaceholder()}>
                {t('guests.givePlusOne')}
              </Button>
            )}
            {guest && (
              <Button
                type="button"
                variant="danger"
                className="ml-auto"
                onClick={() => void onDelete()}
              >
                {t('guests.remove')}
              </Button>
            )}
          </div>
        </fieldset>
      </form>
    </Drawer>
  );
}
